import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'

import useRequestPending from 'shared/hooks/useRequestPending'
import CircleLoader from 'shared/components/CircleLoader'
import ErrorMessage from 'shared/components/ErrorMessage'
import Pin from 'shared/components/Pin'
import { isDesktop } from 'shared/responsive-helpers'
import { MarkerStyle } from 'shared/generate-marker-style'
import { useConfirm } from 'shared/hooks/useConfirm'
import featureToggles from 'shared/featureToggles'
import scrollTop from 'shared/scroll-top'
import { useSendGTAGEvent } from 'analytics/utils'

import withRates from '@booking/components/withRates'
import { Map } from 'booking/components/Map'
import { ParkingInfoSideBar } from 'booking/components/ParkingInfoSideBar'
import {
    clearSelectedRate,
    selectSeasonTicket,
    setLastBookingDetails,
    lastBookingRemovePromoCode,
    setRawPromoCode,
} from '@booking/actions'
import { clearGuestReservationsState } from 'manage-guest-user-booking/actions'

import { numberOfItemsSelector } from 'basket/selectors'
import { rateSelector, waitingListKeySelector, selectedTicketRatePendingSelector } from '@booking/selectors'

import { BOOKING_FLOW_HEADER_PREFIX, BOOKING_FLOW, EVENT_FLOW } from '@booking/constants'
import { SEARCH_MODE_EVENTS, SEARCH_MODE_HOURLY } from 'search/constants'

import PurchaseFlowHeader from 'purchase-flow/components/PurchaseFlowHeader/PurchaseFlowHeader'
import SuccessPage from 'purchase-flow/components/SuccessPage'

import {
    FLOW_STEP_DATE,
    FLOW_STEP_PRODUCT,
    FLOW_STEP_SUCCESS,
    FLOW_STEP_VEHICLE,
    FLOW_STEP_EVENT,
} from 'purchase-flow/constants'
import { credentialTypeSelector } from 'purchase-flow/canada/selectors'

import { HourlyFlow } from './HourlyFlow'
import { EventFlowBr } from '../../EventsSelectionBr/EventFlowBr'

import styles from 'purchase-flow/styles.module.scss'
import bookingStyles from './styles.module.scss'
import { cancellationSelector, reservationWithStatusSelector } from 'dashboard-transactions/selectors'
import { reservationSelector } from 'grs/selectors'

const initialState = {
    startDate: null,
    product: null,
    event: null,
    vehicles: null,
    details: null,
}

const $BookingSelection = ({
    detailsPending,
    errorMessage,
    ticketsPending,
    rate,
    clearSelectedRate,
    grsId,
    locationDetails,
    setLastBookingDetails,
    waitingListKey,
    numberOfItemsInBasket,
    rateVehicle,
    lastBookingRemovePromoCode,
    setRawPromoCode,
    parkingMode,
    defaultCustomerFlow,
    clearGuestReservationsState,
    reservationAmendPolicy,
}) => {
    const { confirm, ConfirmModal } = useConfirm()
    const location = useLocation()
    const { isLoading: ticketsLoading } = useRequestPending(ticketsPending)
    const { isLoading: locationLoading } = useRequestPending(detailsPending)
    const sendGTAGEvent = useSendGTAGEvent()
    const [currentItemIndex] = React.useState(numberOfItemsInBasket + 1)
    const isHourlyFlow = parkingMode === SEARCH_MODE_HOURLY
    const activeStepsFlow = isHourlyFlow ? BOOKING_FLOW : EVENT_FLOW
    const showPixPaymentMethod = featureToggles.hasPIXPurchase && (isHourlyFlow || parkingMode === SEARCH_MODE_EVENTS)
    const showContent = !(locationLoading || ticketsLoading) && !errorMessage

    const addGuestCheckoutFlowClassName = () => featureToggles.hasGuestCheckoutFlow && activeStep === FLOW_STEP_VEHICLE

    const [activeStep, setActiveStep] = useState(null)
    const [bookingData, setBookingData] = useState(initialState)
    const { geoLocation, name } = locationDetails

    useEffect(() => {
        setActiveStep(isHourlyFlow ? FLOW_STEP_DATE : FLOW_STEP_EVENT)
    }, [parkingMode])

    useEffect(() => {
        clearSelectedRate()
    }, [])

    useEffect(() => {
        if (locationDetails?.name) {
            sendGTAGEvent('select_location', {
                location_name: locationDetails.name,
            })
        }
    }, [locationDetails])

    useEffect(() => {
        setLastBookingDetails({
            id: currentItemIndex,
            grsId,
            rate,
            waitingListKey,
        })
    }, [grsId, rate, waitingListKey])

    useEffect(() => {
        scrollTop()
    }, [activeStep])

    useEffect(() => {
        setBookingData(initialState)
    }, [parkingMode])

    useEffect(() => {
        const currentPath = location.pathname

        return () => {
            clearGuestReservationsState()
        }
    }, [location?.pathname])

    const onNext = () => {
        const newIndex = activeStepsFlow.indexOf(activeStep) + 1

        if (newIndex < activeStepsFlow.length) {
            setActiveStep(activeStepsFlow[newIndex])
        }
    }

    const setStartDate = startDate => {
        setBookingData({ ...bookingData, startDate })
        sendGTAGEvent('select_date', {
            start_date: startDate.startDate.format('YYYY/MM/DD HH:mm:s'),
            end_date: startDate.endDate.format('YYYY/MM/DD HH:mm:s'),
        })
    }

    const setProduct = product => {
        setBookingData({ ...bookingData, product })
    }

    const setVehicle = vehicles => {
        setBookingData({ ...bookingData, vehicles })
    }

    const setEvent = event => {
        setBookingData({ ...bookingData, event })
    }

    const setDateRange = date => {
        setBookingData({ ...bookingData, date })
    }

    const setOutboundNumber = data => {
        setBookingData({ ...bookingData, outboundNumber: data.outboundNumber })
    }

    const setInboundNumber = data => {
        setBookingData({ ...bookingData, inboundNumber: data.inboundNumber })
    }

    const setAdditionalServices = data => {
        setBookingData({ ...bookingData, additionalServices: data })
    }

    const onStepClick = async step => {
        const { startDate } = bookingData
        let { date, product, delivery, vehicles, event, outboundNumber, inboundNumber, additionalServices } =
            bookingData

        if (step === FLOW_STEP_PRODUCT) {
            product = null
            vehicles = null
            delivery = null
            outboundNumber = null
            inboundNumber = null
            additionalServices = null
            clearSelectedRate()
        }

        if (step === FLOW_STEP_VEHICLE) {
            vehicles = null
            delivery = null
            outboundNumber = null
            inboundNumber = null
        }

        if (step === FLOW_STEP_DATE) {
            const isConfirmed = await confirm()
            if (!isConfirmed) {
                return
            }
            product = null
            vehicles = null
            delivery = null
            outboundNumber = null
            inboundNumber = null
            additionalServices = null
        }

        if (step === FLOW_STEP_EVENT) {
            const isConfirmed = await confirm()
            if (!isConfirmed) {
                return
            }
            date = null
            event = null
            vehicles = null
        }

        setRawPromoCode('')
        lastBookingRemovePromoCode()
        setActiveStep(step)
        setBookingData({
            ...bookingData,
            startDate,
            product,
            delivery,
            vehicles,
            date,
            event,
            outboundNumber,
            inboundNumber,
            additionalServices,
        })
    }

    if (!bookingData.inboundNumber && reservationAmendPolicy?.inboundFlightNumber) {
        setInboundNumber({ inboundNumber: reservationAmendPolicy.inboundFlightNumber })
    }

    if (!bookingData.outboundNumber && reservationAmendPolicy?.outboundFlightNumber) {
        setOutboundNumber({ outboundNumber: reservationAmendPolicy.outboundFlightNumber })
    }

    return (
        <div
            className={classNames(
                styles.purchaseContainer,
                addGuestCheckoutFlowClassName() && styles.guestCheckoutVehicleContainer
            )}>
            <ConfirmModal
                title="subscription.purchase.step.confirm.title"
                text="subscription.purchase.step.confirm.text"
            />
            <div className={styles.purchaseScreen}>
                {activeStep === FLOW_STEP_SUCCESS ? (
                    <SuccessPage href="/dashboard/transactions" />
                ) : (
                    <div className={classNames(styles.flowContainer)}>
                        {(locationLoading || ticketsLoading) && <CircleLoader />}
                        {errorMessage && <ErrorMessage messages={[errorMessage]} />}
                        {showContent && (
                            <div className={classNames(bookingStyles.flowSection)}>
                                <PurchaseFlowHeader
                                    grsId={grsId}
                                    parkingMode={parkingMode}
                                    defaultCustomerFlow={defaultCustomerFlow}
                                    activeStep={activeStep}
                                    stepValues={bookingData}
                                    locationName={name}
                                    onStepClick={onStepClick}
                                    flowSteps={activeStepsFlow}
                                    stepPrefix={BOOKING_FLOW_HEADER_PREFIX}
                                />
                                {isHourlyFlow ? (
                                    <HourlyFlow
                                        activeStep={activeStep}
                                        grsId={grsId}
                                        onNext={onNext}
                                        setStartDate={setStartDate}
                                        setProduct={setProduct}
                                        bookingData={bookingData}
                                        setVehicle={setVehicle}
                                        rate={rate}
                                        currentItemIndex={currentItemIndex}
                                        rateVehicle={rateVehicle}
                                        errorMessage={errorMessage}
                                        waitingListKey={waitingListKey}
                                        setOutboundNumber={setOutboundNumber}
                                        setInboundNumber={setInboundNumber}
                                        setAdditionalServices={setAdditionalServices}
                                        showPixPaymentMethod={showPixPaymentMethod}
                                    />
                                ) : (
                                    <EventFlowBr
                                        grsId={grsId}
                                        activeStep={activeStep}
                                        bookingData={bookingData}
                                        onNext={onNext}
                                        setDateRange={setDateRange}
                                        setEvent={setEvent}
                                        setVehicle={setVehicle}
                                        rateVehicle={rateVehicle}
                                        errorMessage={errorMessage}
                                        currentItemIndex={currentItemIndex}
                                        rate={rate}
                                        showPixPaymentMethod={showPixPaymentMethod}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isDesktop() && showContent && (
                <div className={styles.sidebar}>
                    <ParkingInfoSideBar loading={detailsPending} location={locationDetails}>
                        {geoLocation && (
                            <Map geoLocation={geoLocation}>
                                <div style={MarkerStyle(30, 30)}>
                                    <Pin name={name} />
                                </div>
                            </Map>
                        )}
                    </ParkingInfoSideBar>
                </div>
            )}
        </div>
    )
}

export const BookingSelection = compose(
    connect(
        createStructuredSelector({
            ratePending: selectedTicketRatePendingSelector,
            credentialType: credentialTypeSelector,
            numberOfItemsInBasket: numberOfItemsSelector,
            waitingListKey: waitingListKeySelector,
            rate: rateSelector,
            reservationAmendPolicy: cancellationSelector,
        }),
        {
            selectSeasonTicket,
            clearSelectedRate,
            setLastBookingDetails,
            lastBookingRemovePromoCode,
            setRawPromoCode,
            clearGuestReservationsState,
        }
    )
)(withRates($BookingSelection))
